import {
  Box,
  useTheme,
  TextField,
  Button,
  styled,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  Collapse,
} from '@mui/material'
import { Warning, ExpandMore } from '@mui/icons-material'
import React, { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { FormField, getFieldKey, IChecklistItemValue } from '../../generated-types/form-field'
import { useTranslation } from 'react-i18next'
import { GetRGBA, getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { GridActionButton } from '../common/GridActionButton'

interface IChecklistFieldProps {
  field: FormField
  disabled?: boolean
  disabledAddDeviation?: boolean
  form?: UseFormReturn
  onDragStart: () => void
  onChange: (values: any) => void
  setSelectedSettingField: () => void
  getItemDeviations?: (fieldId: string, itemId: string) => any[]
  handleOpenDeviationMenu?: (event: React.MouseEvent<HTMLElement>, fieldId: string, itemId: string) => void
  handleOpenAddDeviationModal?: (event: React.MouseEvent<HTMLElement>, fieldId: string, itemId: string) => void
  customerId?: EntityId
  scheduledTaskId?: number
  reportId?: number
}

export const ChecklistField: React.FC<IChecklistFieldProps> = ({
  field,
  disabled,
  disabledAddDeviation,
  form,
  onDragStart,
  onChange,
  getItemDeviations,
  handleOpenDeviationMenu,
  handleOpenAddDeviationModal,
}) => {
  const { t } = useTranslation(['form-schema', 'common', 'checklist', 'deviation'])
  const theme = useTheme()
  const values = form?.watch()
  const [commentOpen, setCommentOpen] = useState<string | null>(null)
  const showDeviationIndicator = Boolean(getItemDeviations && handleOpenDeviationMenu)

  const getChecklistValues = (): IChecklistItemValue[] => {
    try {
      const stored = values?.[getFieldKey(field)]
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  }

  const checklistValues = getChecklistValues()

  const getItemValue = (itemId: string): IChecklistItemValue | undefined => {
    return checklistValues.find(v => v.id === itemId)
  }

  const updateItemValue = (itemId: string, updates: Partial<IChecklistItemValue>) => {
    const currentValues = getChecklistValues()
    const existingIndex = currentValues.findIndex(v => v.id === itemId)

    if (existingIndex >= 0) {
      currentValues[existingIndex] = { ...currentValues[existingIndex], ...updates }
    } else {
      currentValues.push({ id: itemId, status: null, ...updates } as IChecklistItemValue)
    }

    onChange(JSON.stringify(currentValues))
  }

  const handleStatusChange = (itemId: string, status: 'ok' | 'notOk' | 'na') => {
    const currentValue = getItemValue(itemId)
    if (currentValue?.status === status) {
      updateItemValue(itemId, { status: null })
    } else {
      updateItemValue(itemId, { status })
    }
  }

  const handleCommentChange = (itemId: string, comment: string) => {
    updateItemValue(itemId, { comment })
  }

  const selectedDisabledTextColor = GetRGBA(getThemeColor(theme, MUIThemeColors.white), 0.7)

  const getStatusTextColor = (isSelected: boolean, activeColor: string, isLocked = false) => {
    if (!isSelected) return disabled || isLocked ? undefined : activeColor
    if (isLocked) return getThemeColor(theme, MUIThemeColors.white)
    return disabled ? selectedDisabledTextColor : getThemeColor(theme, MUIThemeColors.white)
  }

  const getStatusButtonSx = (isSelected: boolean, isLocked = false) => ({
    minWidth: 80,
    ...(isLocked && isSelected && { '& *': { color: getThemeColor(theme, MUIThemeColors.white) } }),
    ...(!isLocked && disabled && isSelected && { '& *': { color: selectedDisabledTextColor } }),
  })

  return (
    <ChecklistContainer draggable={true} onDragStart={onDragStart}>
      <Stack spacing={0}>
        {field.checklistItems?.map((item, index) => {
          const itemValue = getItemValue(item.id)
          const status = itemValue?.status
          const isNotOk = status === 'notOk'
          const itemDeviations = showDeviationIndicator ? getItemDeviations!(getFieldKey(field), item.id) : []
          // A row with a deviation on it stays on Not OK until that deviation is removed.
          const isLockedByDeviation = itemDeviations.length > 0
          const isFirst = index === 0
          const isLast = index === (field.checklistItems?.length || 0) - 1

          return (
            <ChecklistItemBox key={item.id} isFirst={isFirst} isLast={isLast}>
              <ItemHeaderStack direction="row" alignItems="center" spacing={1}>
                <ItemQuestionText variant="body2">
                  {item.question}
                  {field.isRequired && <RequiredAsterisk>*</RequiredAsterisk>}
                </ItemQuestionText>
                {showDeviationIndicator && itemDeviations.length > 0 && (
                  <Tooltip title={`${itemDeviations.length} ${t('deviations', { ns: 'deviation' })}`}>
                    <IconButton
                      size="small"
                      onClick={e => handleOpenDeviationMenu!(e, getFieldKey(field), item.id)}
                      sx={{
                        color: getThemeColor(theme, MUIThemeColors.errorMain),
                        p: 0.5,
                        '&:hover': {
                          backgroundColor: getThemeColor(theme, MUIThemeColors.errorLight) + '40',
                        },
                      }}
                    >
                      <Warning fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </ItemHeaderStack>
              <Tooltip
                title={
                  isLockedByDeviation && !disabled ? (t('status-locked-by-deviation', { ns: 'checklist' }) as string) : ''
                }
              >
                <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
                  <GridActionButton
                    buttonText={t('ok', { ns: 'checklist' })}
                    bgColor={status === 'ok' ? getThemeColor(theme, MUIThemeColors.primaryMain) : undefined}
                    textColor={getStatusTextColor(
                      status === 'ok',
                      getThemeColor(theme, MUIThemeColors.primaryMain),
                      disabled || isLockedByDeviation,
                    )}
                    onClick={() => handleStatusChange(item.id, 'ok')}
                    disabled={disabled || isLockedByDeviation}
                    sx={getStatusButtonSx(status === 'ok', isLockedByDeviation)}
                  />
                  <GridActionButton
                    buttonText={t('not-ok', { ns: 'checklist' })}
                    bgColor={status === 'notOk' ? getThemeColor(theme, MUIThemeColors.errorMain) : undefined}
                    textColor={getStatusTextColor(
                      status === 'notOk',
                      getThemeColor(theme, MUIThemeColors.errorMain),
                      disabled || isLockedByDeviation,
                    )}
                    onClick={() => handleStatusChange(item.id, 'notOk')}
                    disabled={disabled || isLockedByDeviation}
                    sx={getStatusButtonSx(status === 'notOk', isLockedByDeviation)}
                  />
                  <GridActionButton
                    buttonText={t('na', { ns: 'checklist' })}
                    bgColor={status === 'na' ? getThemeColor(theme, MUIThemeColors.primaryMain) : undefined}
                    textColor={getStatusTextColor(
                      status === 'na',
                      getThemeColor(theme, MUIThemeColors.primaryMain),
                      disabled || isLockedByDeviation,
                    )}
                    onClick={() => handleStatusChange(item.id, 'na')}
                    disabled={disabled || isLockedByDeviation}
                    sx={getStatusButtonSx(status === 'na', isLockedByDeviation)}
                  />
                </Stack>
              </Tooltip>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mb: 0.5,
                  pt: 0.75,
                }}
              >
                {(!disabled || itemValue?.comment) && (
                  <AddCommentButton
                    variant="text"
                    size="small"
                    startIcon={!disabled ? <ExpandMoreIcon isOpen={commentOpen === item.id} /> : undefined}
                    onClick={() => !disabled && setCommentOpen(commentOpen === item.id ? null : item.id)}
                    disabled={disabled && !itemValue?.comment}
                  >
                    {disabled ? t('comment', { ns: 'checklist' }) : t('add-comment', { ns: 'checklist' })}
                  </AddCommentButton>
                )}
                {isNotOk && !disabledAddDeviation && !disabled && (
                  <DeviationButton
                    variant={itemValue?.deviation ? 'contained' : 'outlined'}
                    hasDeviation={!!itemValue?.deviation}
                    disabled={disabled || !isNotOk}
                    size="small"
                    onClick={e => handleOpenAddDeviationModal?.(e, getFieldKey(field), item.id)}
                  >
                    {t('deviation', { ns: 'deviation' })}
                  </DeviationButton>
                )}
              </Stack>
              <Collapse in={commentOpen === item.id || (disabled && !!itemValue?.comment)}>
                <CommentCollapseBox>
                  <TextField
                    size="small"
                    placeholder={t('add-comment', { ns: 'checklist' })}
                    value={itemValue?.comment || ''}
                    onChange={e => handleCommentChange(item.id, e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                    disabled={disabled}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </CommentCollapseBox>
              </Collapse>
            </ChecklistItemBox>
          )
        })}
        {(!field.checklistItems || field.checklistItems.length === 0) && (
          <EmptyStateBox>
            <EmptyStateText variant="body2">
              {disabled
                ? t('add-checklist-options', { ns: 'checklist' })
                : t('no-checklist-items', { ns: 'checklist' })}
            </EmptyStateText>
          </EmptyStateBox>
        )}
      </Stack>
    </ChecklistContainer>
  )
}

const ChecklistContainer = styled(Box)(() => ({
  draggable: true,
}))

const ChecklistItemBox = styled(Box)<{ isFirst?: boolean; isLast?: boolean }>(({ theme, isFirst, isLast }) => ({
  border: `1px solid ${GetRGBA(getThemeColor(theme, MUIThemeColors.black), 0.1)}`,
  borderTop: isFirst ? `1px solid ${GetRGBA(getThemeColor(theme, MUIThemeColors.black), 0.1)}` : 'none',
  borderRadius: isFirst && isLast ? '4px' : isFirst ? '4px 4px 0 0' : isLast ? '0 0 4px 4px' : 0,
  padding: '10px',
  marginTop: isFirst ? '10px' : 0,
}))

const ItemHeaderStack = styled(Stack)(() => ({
  marginBottom: '8px',
}))

const ItemQuestionText = styled(Typography)(() => ({
  fontWeight: 500,
}))

const RequiredAsterisk = styled('span')(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.errorMain),
  marginLeft: '2px',
}))

const ExpandMoreIcon = styled(ExpandMore)<{ isOpen?: boolean }>(({ isOpen }) => ({
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.3s',
  fontSize: '1rem',
}))

const AddCommentButton = styled(Button)(({ theme }) => ({
  justifyContent: 'flex-start',
  color: getThemeColor(theme, MUIThemeColors.secondaryMain),
  padding: '2px',
  textTransform: 'none',
  fontSize: '0.875rem',
}))

const CommentCollapseBox = styled(Box)(() => ({
  paddingLeft: '4px',
  paddingRight: '4px',
  paddingTop: '2px',
}))

const EmptyStateBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${GetRGBA(getThemeColor(theme, MUIThemeColors.black), 0.1)}`,
  borderRadius: '4px',
  padding: '10px',
  marginTop: '10px',
}))

const EmptyStateText = styled(Typography)(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.secondaryMain),
  paddingY: '32px',
  textAlign: 'center',
}))

const DeviationButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'hasDeviation',
})<{ hasDeviation?: boolean }>(({ theme, hasDeviation }) => ({
  textTransform: 'none',
  borderColor: getThemeColor(theme, MUIThemeColors.primaryMain),
  color: hasDeviation ? getThemeColor(theme, MUIThemeColors.white) : getThemeColor(theme, MUIThemeColors.primaryMain),
  backgroundColor: hasDeviation ? getThemeColor(theme, MUIThemeColors.primaryMain) : 'transparent',

  '&:hover': {
    backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain),
    color: getThemeColor(theme, MUIThemeColors.white),
  },
}))
